import { useState, useEffect, Dispatch, SetStateAction } from "react";
import type { FC } from "react";
import { Modal, Button, TextField, CardMedia } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { uploadService } from "../api/services/uploadService";
import { showError, showSuccess } from "../helpers/toast";

export interface AddEquipmentModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  title?: string;
  initialName?: string;
  initialImage?: string;
  onSubmit?: (payload: { name: string; image?: string }) => Promise<void> | void;
  isSubmitting?: boolean;
}

const AddEquipment: FC<AddEquipmentModalProps> = ({
  open,
  onClose,
  setOpen,
  title,
  initialName = "",
  initialImage = "",
  onSubmit,
  isSubmitting,
}) => {
  const [equipmentName, setEquipmentName] = useState(initialName);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(initialImage || "");
  const [imageUrl, setImageUrl] = useState<string>(initialImage || "");
  const [error, setError] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (open) {
      setEquipmentName(initialName || "");
      setImageFile(null);
      setImagePreview(initialImage || "");
      setImageUrl(initialImage || "");
      setError(false);
    }
  }, [open, initialName, initialImage]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes("image")) {
      showError("Please select an image file");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const resetState = () => {
    setEquipmentName("");
    setImageFile(null);
    setImagePreview("");
    setImageUrl("");
    setError(false);
    setUploadingImage(false);
  };

  const handleSubmit = async () => {
    setError(true);
    if (equipmentName.trim() === "") return;

    let finalImageUrl = imageUrl;

    if (imageFile) {
      try {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append("file", imageFile);
        const response = await uploadService.uploadFile(formData);
        const data = response?.data;
        if (typeof data === "string") {
          finalImageUrl = data;
        } else if (data && typeof data === "object" && (data as any).url) {
          finalImageUrl = (data as any).url;
        }
        if (finalImageUrl) {
          setImageUrl(finalImageUrl);
          showSuccess("Image uploaded successfully");
        } else {
          showError("Image upload failed");
          setUploadingImage(false);
          return;
        }
      } catch (err: any) {
        showError(err?.response?.data?.message || "Failed to upload image");
        setUploadingImage(false);
        return;
      } finally {
        setUploadingImage(false);
      }
    }

    try {
      await onSubmit?.({ name: equipmentName.trim(), image: finalImageUrl });
      setOpen(false);
      resetState();
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || "Failed to submit");
    }
  };

  return (
    <Modal
      className="modal addNotification_modal"
      open={open}
      onClose={() => {
        onClose();
        resetState();
      }}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{title || "Add Equipment"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form">
            <div className="control_group">
              <TextField
                placeholder="Equipment Name"
                fullWidth
                value={equipmentName}
                onChange={(e) => setEquipmentName(e.target.value)}
                hiddenLabel
              />
              {error && equipmentName === "" && (
                <h6 className="err_msg">Equipment name is required</h6>
              )}
            </div>

            {/* <div className="control_group">
              <TextField
                placeholder="Training Type"
                fullWidth
                value={trainingType}
                onChange={(e) => setTrainingType(e.target.value)}
                hiddenLabel
              />
              {error && trainingType === "" && (
                <h6 className="err_msg">Training type is required</h6>
              )}
            </div> */}

            <div className="control_group">
              <label>Equipment Image</label>
              {imagePreview ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={imagePreview}
                        alt="Equipment"
                        sx={{ maxHeight: "200px", objectFit: "contain" }}
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview("");
                        setImageUrl("");
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="equipment-file-input">
                  <input
                    id="equipment-file-input"
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/user_placeholder.png" alt="placeholder" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="upload" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => {
                  setOpen(false);
                  resetState();
                }}
              >
                Cancel
              </Button>

              <Button
                className="btn btn_primary"
                onClick={handleSubmit}
                disabled={uploadingImage || isSubmitting}
              >
                {uploadingImage || isSubmitting ? "Saving..." : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddEquipment;
